import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useGame } from "../context/GameContext";
import PageLayout from "../components/PageLayout";
import MdGlowButton from "../components/MdGlowButton";
import { PlayCircle, Lightbulb, Mic, Timer } from "lucide-react";

const CommentJouer = () => {
  const navigate = useNavigate();
  const { token } = useGame();

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  return (
    <PageLayout wide title="Casting Le Maillon Faible">
      <div className="text-center fade-up max-w-2xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-3">
          Comment <span className="text-md-cyan">jouer ?</span>
        </h2>
        <p className="text-white/55 text-sm sm:text-base leading-relaxed">
          La caméra enregistre votre manche de rapidité du début à la fin. Répondez à voix haute, sans
          hésiter — le chrono est serré.
        </p>
      </div>

      <section className="mt-8 max-w-2xl mx-auto space-y-3 text-left fade-up" data-testid="comment-jouer-tutorial">
        <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="comment-jouer-step-1">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
            1
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="text-white/90">
              Vous enchaînez <strong className="text-md-cyan">plusieurs questions ouvertes</strong>. Entre
              chaque question, un mouvement de caméra (flou de déplacement) simule le passage au
              candidat suivant — c&apos;est toujours vous à l&apos;écran.
            </p>
          </div>
        </div>

        <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="comment-jouer-step-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
            2
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="text-white/90 flex items-start gap-2">
              <Mic size={16} className="mt-0.5 shrink-0 text-md-cyan" />
              <span>
                Une question est lue (audio) puis affichée.{" "}
                <strong className="text-white">Répondez clairement à voix haute</strong>, puis validez
                avec le bouton « J&apos;ai répondu » (ou Espace sur ordinateur).
              </span>
            </p>
          </div>
        </div>

        <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="comment-jouer-step-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
            3
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="text-white/90 flex items-start gap-2">
              <Timer size={16} className="mt-0.5 shrink-0 text-md-cyan" />
              <span>
                Sans réponse sous <strong className="text-white">5 secondes</strong>, on passe
                automatiquement à la question suivante.
              </span>
            </p>
          </div>
        </div>

        <div
          className="md-card p-4 sm:p-5 flex gap-3 border border-amber-400/25 bg-amber-400/5"
          data-testid="comment-jouer-tip-haute-voix"
        >
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400/20 text-amber-300"
            aria-hidden="true"
          >
            <Lightbulb size={15} strokeWidth={2.25} />
          </span>
          <div className="text-sm leading-relaxed pt-0.5 min-w-0">
            <p className="font-semibold text-amber-200 mb-1">Conseil pour réussir votre casting</p>
            <p className="text-white/80">
              Parlez <strong className="text-white font-bold underline decoration-amber-300/80 underline-offset-2">
                fort et distinctement
              </strong>
              . La production évalue votre réactivité et votre présence à l&apos;écran.
            </p>
          </div>
        </div>
      </section>

      <div className="mt-10 flex justify-center fade-up">
        <MdGlowButton onClick={() => navigate("/camera")} data-testid="comment-jouer-continue">
          <span className="inline-flex items-center gap-2">
            <PlayCircle size={18} /> Continuer
          </span>
        </MdGlowButton>
      </div>
    </PageLayout>
  );
};

export default CommentJouer;
